import React, { useEffect } from "react";
import Select from 'react-select';
import { useSelector } from "react-redux";
import { RootState } from "../../slices/store";

export type OptionType = {
    value: string;
    label: string;
};

const timeOptions: OptionType[] = [
    { value: "day", label: "Daily" },
    { value: "week", label: "Weekly" },
    { value: "month", label: "Monthly" },
    { value: "quarter", label: "Quarterly" },
    { value: "year", label: "Yearly" },
];


interface Props {
    selectedOption: string | '';
    onChange: (option: OptionType | null) => void;
    startDate?: string;
    endDate?: string;
}


const DataByDropdown = ({
    selectedOption,
    onChange,
    startDate: startDateProp,
    endDate: endDateProp,
}: Props) => {
    const filters = useSelector((state: RootState) => state.filters);
    const startDate = startDateProp ?? filters.startDate;
    const endDate = endDateProp ?? filters.endDate;


    const getDaysDiff = (startDate: string | Date, endDate: string | Date): number => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    };

    // Determines if a time option should be shown based on rules
    const shouldShowTimeOption = (optionValue: string): boolean => {
        const daysDiff = getDaysDiff(startDate, endDate);
        const getDaysInMonth = (dateStr: string): number => {
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = date.getMonth(); // 0-indexed
            return new Date(year, month + 1, 0).getDate(); // Last day of the month
        };

        const startMonthDays = getDaysInMonth(startDate);
        const endMonthDays = getDaysInMonth(endDate);
        const minMonthDays = Math.min(startMonthDays, endMonthDays);

        // selected range is < 7 days, only show 'day'
        if (daysDiff < 7) {
            return optionValue === 'day';
        }

        // range is less than the highest day of start/end date, show 'day' and 'week'
        if (daysDiff < minMonthDays) {
            return ['day', 'week'].includes(optionValue);
        }

        //range is less than 92 days (approx. 3 months):
        if (daysDiff < 92) {
            return ['day', 'week', 'month'].includes(optionValue);
        }

        //If range < 365 days (~1 year), show 'week', 'month', 'quarter'
        if (daysDiff < 365) {
            return ['day','week', 'month', 'quarter'].includes(optionValue);
        }

        //For 1 year or more, show all except 'day'
        return ['day','week', 'month', 'quarter', 'year'].includes(optionValue);
    };

    // Filter options based on the rules
    const filteredOptions = timeOptions.filter(opt => shouldShowTimeOption(opt.value));
    const selectedTimeOption = filteredOptions.find(opt => opt.value === selectedOption);

    //  Automatically set first available option if selectedOption is no longer valid
    useEffect(() => {
        // Debounce to allow Redux updates to propagate before auto-correcting
        const timer = setTimeout(() => {
            if (!selectedTimeOption && filteredOptions.length > 0) {
                onChange(filteredOptions[0]); // force update to first valid option
            }
        }, 50);

        return () => clearTimeout(timer);
    }, [filteredOptions, selectedOption, selectedTimeOption, onChange]);

    return (
        <Select
            className="dropdown week-select"
            classNamePrefix="react-select"
            value={selectedTimeOption}
            onChange={onChange}
            options={filteredOptions}
            styles={{
                input: (provided: any) => ({
                    ...provided,
                    caretColor: 'transparent', // Hide cursor
                }),
                control: (base: any) => ({
                    ...base,
                }),
                singleValue: (base: any) => ({
                    ...base,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100% - 8px)',
                }),
                option: (base: any) => ({
                    ...base,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    backgroundColor: 'transparent',
                    color: 'inherit',
                    ':active': {
                     backgroundColor: 'transparent',
                     },
                }),
                menu: (base: any) => ({
                    ...base,
                    width: 'auto',
                    minWidth: '120px',
                    maxWidth: '100vw',
                    zIndex: 1000,
                }),
            }}
        />
    );
};

export default DataByDropdown;
